import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import {
  AdminDevice,
  ClientGroupOption,
  DeviceCreateRequest,
  DeviceUpdateRequest,
} from '../../types/adminDevice';

type Mode = 'create' | 'edit';

interface DeviceFormDialogProps {
  open: boolean;
  mode: Mode;
  device?: AdminDevice | null;
  clientGroups: ClientGroupOption[];
  saving: boolean;
  onClose: () => void;
  onCreate: (payload: DeviceCreateRequest) => void;
  onUpdate: (serialNo: string, payload: DeviceUpdateRequest) => void;
}

interface FormState {
  serialNo: string;
  city: string;
  location: string;
  latitude: string;
  longitude: string;
  model: string;
  version: string;
  status: string;
  userName: string;
  lastSentRouteDate: string;
  lastSentRouteShipmentNumber: string;
}

const emptyForm: FormState = {
  serialNo: '',
  city: '',
  location: '',
  latitude: '',
  longitude: '',
  model: '',
  version: '',
  status: '',
  userName: '',
  lastSentRouteDate: '',
  lastSentRouteShipmentNumber: '',
};

const DeviceFormDialog: React.FC<DeviceFormDialogProps> = ({
  open,
  mode,
  device,
  clientGroups,
  saving,
  onClose,
  onCreate,
  onUpdate,
}) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [groupMode, setGroupMode] = useState<'existing' | 'new'>('existing');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [newGroup, setNewGroup] = useState({ name: '', clientId: '', clientSecret: '' });
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setValidationError(null);
    if (mode === 'edit' && device) {
      setForm({
        serialNo: device.serialNo,
        city: device.city ?? '',
        location: device.location ?? '',
        latitude: device.latitude ?? '',
        longitude: device.longitude ?? '',
        model: device.model ?? '',
        version: device.version ?? '',
        status: device.status ?? '',
        userName: device.userName ?? '',
        lastSentRouteDate: device.lastSentRouteDate ?? '',
        lastSentRouteShipmentNumber: device.lastSentRouteShipmentNumber ?? '',
      });
      setSelectedClientId(device.clientId ?? '');
      setGroupMode('existing');
      setNewGroup({ name: '', clientId: '', clientSecret: '' });
    } else {
      setForm(emptyForm);
      setSelectedClientId('');
      setNewGroup({ name: '', clientId: '', clientSecret: '' });
      // Default to creating a new group only if none exist yet.
      setGroupMode(clientGroups.length > 0 ? 'existing' : 'new');
    }
  }, [open, mode, device, clientGroups.length]);

  const handleField = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const buildMetadata = () => ({
    city: form.city.trim() || undefined,
    location: form.location.trim() || undefined,
    latitude: form.latitude.trim() || undefined,
    longitude: form.longitude.trim() || undefined,
    model: form.model.trim() || undefined,
    version: form.version.trim() || undefined,
    status: form.status.trim() || undefined,
    userName: form.userName.trim() || undefined,
    lastSentRouteDate: form.lastSentRouteDate.trim() || undefined,
    lastSentRouteShipmentNumber: form.lastSentRouteShipmentNumber.trim() || undefined,
  });

  const handleSubmit = () => {
    setValidationError(null);

    if (mode === 'create') {
      if (!form.serialNo.trim() || form.serialNo.trim().length < 4) {
        setValidationError('Serial number is required (4-20 characters)');
        return;
      }

      const payload: DeviceCreateRequest = {
        serialNo: form.serialNo.trim(),
        ...buildMetadata(),
      };

      if (groupMode === 'existing') {
        if (!selectedClientId) {
          setValidationError('Please select a client group to attach this device to (OAuth required)');
          return;
        }
        payload.clientId = selectedClientId;
      } else {
        if (!newGroup.name.trim() || !newGroup.clientId.trim() || !newGroup.clientSecret.trim()) {
          setValidationError('New client group requires name, client ID and client secret');
          return;
        }
        payload.newClientGroup = {
          name: newGroup.name.trim(),
          clientId: newGroup.clientId.trim(),
          clientSecret: newGroup.clientSecret.trim(),
        };
      }

      onCreate(payload);
      return;
    }

    // Edit mode
    if (!device) return;
    const payload: DeviceUpdateRequest = { ...buildMetadata() };
    // Only send clientId if it changed (reassignment).
    if (selectedClientId && selectedClientId !== (device.clientId ?? '')) {
      payload.clientId = selectedClientId;
    }
    onUpdate(device.serialNo, payload);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Create Device' : `Edit Device — ${device?.serialNo}`}</DialogTitle>
      <DialogContent dividers>
        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="Serial Number"
            value={form.serialNo}
            onChange={handleField('serialNo')}
            disabled={mode === 'edit' || saving}
            required={mode === 'create'}
            helperText={mode === 'edit' ? 'Serial number cannot be changed' : '4-20 characters'}
            fullWidth
          />
          <TextField label="Model" value={form.model} onChange={handleField('model')} disabled={saving} fullWidth />
          <TextField label="City" value={form.city} onChange={handleField('city')} disabled={saving} fullWidth />
          <TextField label="Location" value={form.location} onChange={handleField('location')} disabled={saving} fullWidth />
          <TextField label="Latitude" value={form.latitude} onChange={handleField('latitude')} disabled={saving} fullWidth />
          <TextField label="Longitude" value={form.longitude} onChange={handleField('longitude')} disabled={saving} fullWidth />
          <TextField label="Version" value={form.version} onChange={handleField('version')} disabled={saving} fullWidth />
          <TextField label="Status" value={form.status} onChange={handleField('status')} disabled={saving} fullWidth />
          <TextField label="Owner / User Name" value={form.userName} onChange={handleField('userName')} disabled={saving} fullWidth />
          <TextField
            label="Last Sent Route"
            value={form.lastSentRouteShipmentNumber}
            onChange={handleField('lastSentRouteShipmentNumber')}
            disabled={saving}
            fullWidth
            helperText={mode === 'create' ? 'Leave blank to auto-generate a random value' : 'Last sent route shipment number'}
          />
          <TextField
            label="Last Sent Route Date"
            value={form.lastSentRouteDate}
            onChange={handleField('lastSentRouteDate')}
            disabled={saving}
            fullWidth
            placeholder="yyyy-MM-dd HH:mm:ss.SSS"
            helperText={mode === 'create' ? 'Leave blank to default to now' : 'Format: yyyy-MM-dd HH:mm:ss.SSS'}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Client Group (OAuth ownership)
          </Typography>

          {mode === 'create' ? (
            <>
              <FormControl sx={{ mb: 1 }}>
                <FormLabel>Assignment</FormLabel>
                <RadioGroup
                  row
                  value={groupMode}
                  onChange={(e) => setGroupMode(e.target.value as 'existing' | 'new')}
                >
                  <FormControlLabel
                    value="existing"
                    control={<Radio />}
                    label="Existing client"
                    disabled={saving || clientGroups.length === 0}
                  />
                  <FormControlLabel value="new" control={<Radio />} label="New client" disabled={saving} />
                </RadioGroup>
              </FormControl>

              {groupMode === 'existing' ? (
                <TextField
                  select
                  label="Select client group"
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  disabled={saving || clientGroups.length === 0}
                  fullWidth
                  helperText={
                    clientGroups.length === 0
                      ? 'No client groups exist yet — create a new one'
                      : 'Device will be accessible via this client\'s OAuth credentials'
                  }
                >
                  {clientGroups.map((g) => (
                    <MenuItem key={g.clientId} value={g.clientId}>
                      {g.name} ({g.clientId})
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <TextField
                    label="Client group name"
                    value={newGroup.name}
                    onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                    disabled={saving}
                    fullWidth
                  />
                  <TextField
                    label="Client ID"
                    value={newGroup.clientId}
                    onChange={(e) => setNewGroup({ ...newGroup, clientId: e.target.value })}
                    disabled={saving}
                    fullWidth
                  />
                  <TextField
                    label="Client Secret"
                    value={newGroup.clientSecret}
                    onChange={(e) => setNewGroup({ ...newGroup, clientSecret: e.target.value })}
                    disabled={saving}
                    fullWidth
                  />
                </Box>
              )}
            </>
          ) : (
            <TextField
              select
              label="Reassign to client group"
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              disabled={saving || clientGroups.length === 0}
              fullWidth
              helperText="Change to move this device to a different client (OAuth ownership)"
            >
              {clientGroups.map((g) => (
                <MenuItem key={g.clientId} value={g.clientId}>
                  {g.name} ({g.clientId})
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : <Save />}
        >
          {mode === 'create' ? 'Create' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceFormDialog;
